
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Play, Pause, Volume2, VolumeX, Maximize, Settings, Captions } from 'lucide-react';
import { useVideoSubtitles } from '@/hooks/useVideoSubtitles';
import { useLanguageContext } from '@/contexts/LanguageContext';
import { useTranslation } from '@/hooks/useDeepLTranslation';

interface VideoPlayerProps {
  videoUrl?: string;
  title: string;
  description: string;
}

export const VideoPlayer = ({ 
  videoUrl = "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4", 
  title, 
  description 
}: VideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [showSubtitles, setShowSubtitles] = useState(true);
  const [showControls, setShowControls] = useState(true);
  const [videoError, setVideoError] = useState(false);
  
  const { currentLanguage } = useLanguageContext();
  const { currentSubtitle, updateCurrentSubtitle, isLoading } = useVideoSubtitles();
  const translatedTitle = useTranslation(title);
  const translatedDescription = useTranslation(description);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => {
      const time = video.currentTime;
      setCurrentTime(time);
      updateCurrentSubtitle(time);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      setVideoError(false);
    };

    const handleEnded = () => {
      setIsPlaying(false);
    };

    const handleError = () => {
      setVideoError(true);
      console.error('Erreur de chargement de la vidéo');
    };

    const handleCanPlay = () => {
      setVideoError(false);
    };

    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('error', handleError);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('error', handleError);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [updateCurrentSubtitle]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video || videoError) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play().catch((error) => {
        console.error('Erreur lors de la lecture:', error);
        setVideoError(true);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (newVolume: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || !duration || videoError) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  return (
    <div className="relative bg-gray-900 rounded-lg overflow-hidden group">
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full aspect-video object-cover"
        poster="/placeholder-video-thumbnail.jpg"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(true)}
        crossOrigin="anonymous"
      >
        <source src={videoUrl} type="video/mp4" />
        <track
          kind="captions"
          srcLang={currentLanguage}
          label={`Sous-titres ${currentLanguage.toUpperCase()}`}
          default
        />
        Votre navigateur ne supporte pas la lecture vidéo.
      </video>

      {/* Error Overlay */}
      {videoError && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <div className="text-center space-y-4 text-white">
            <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Play className="w-8 h-8 text-white ml-1" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">{translatedTitle}</h3>
              <p className="text-sm opacity-90">{translatedDescription}</p>
              <p className="text-xs text-gray-300">Vidéo de démonstration temporairement indisponible</p>
            </div>
          </div>
        </div>
      )}

      {/* Placeholder Overlay (when no real video) */}
      {!videoError && videoUrl.includes('placeholder') && (
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-blue-600/20 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Play className="w-8 h-8 text-white ml-1" />
            </div>
            <div className="text-white space-y-2">
              <h3 className="text-xl font-semibold">{translatedTitle}</h3>
              <p className="text-sm opacity-90">{translatedDescription}</p>
            </div>
          </div>
        </div>
      )}

      {/* Subtitles Overlay */}
      {showSubtitles && currentSubtitle && !videoError && (
        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-black/80 text-white text-center px-4 py-2 rounded max-w-md">
            <p className="text-sm leading-relaxed">
              {isLoading ? 'Chargement des sous-titres...' : currentSubtitle}
            </p>
          </div>
        </div>
      )}

      {/* Controls Overlay */}
      {showControls && !videoError && (
        <div className="absolute bottom-4 left-4 right-4 z-30">
          <div className="bg-black/50 backdrop-blur-sm rounded-lg p-3">
            {/* Progress Bar */}
            <div className="mb-3">
              <div 
                className="w-full bg-white/20 rounded-full h-2 cursor-pointer"
                onClick={handleSeek}
              >
                <div 
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                />
              </div>
            </div>

            {/* Control Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  onClick={togglePlay}
                >
                  {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                </Button>
                
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  onClick={toggleMute}
                >
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </Button>
                
                <span className="text-white text-sm">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  className={`text-white hover:bg-white/20 ${showSubtitles ? 'bg-white/20' : ''}`}
                  onClick={() => setShowSubtitles(!showSubtitles)}
                >
                  <Captions className="w-4 h-4" />
                </Button>
                
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                >
                  <Settings className="w-4 h-4" />
                </Button>
                
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-white hover:bg-white/20"
                  onClick={toggleFullscreen}
                >
                  <Maximize className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
